<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">進銷存設定</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" :disabled="saving" @click="handleSave">
          <span v-if="saving">儲存中...</span>
          <span v-else>儲存設定</span>
        </button>
        <span v-if="saved" class="save-success">
          <i class="ri-check-line"></i> 已儲存
        </span>
      </div>
    </div>

    <div class="page-container">

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <form v-else @submit.prevent="handleSave" class="settings-form">

      <!-- 新客戶首次設定提示 -->
      <div v-if="isFirstTime" class="first-time-banner">
        <div class="ftb-icon"><i class="ri-information-line"></i></div>
        <div class="ftb-body">
          <p class="ftb-title">您尚未儲存進銷存設定，目前使用系統預設值</p>
          <ul class="ftb-defaults">
            <li><strong>扣減模式：</strong>簡易庫存模式（訂單直接扣減商品頁庫存數量）</li>
            <li><strong>庫存不足行為：</strong>詢問是否缺貨下訂</li>
          </ul>
          <p class="ftb-hint">請依照您的業務需求調整設定後，按下「儲存設定」。</p>
        </div>
      </div>

      <!-- 兩欄佈局（寬螢幕左右分欄，窄螢幕上下堆疊） -->
      <div class="settings-layout">
        <div class="settings-left">

      <!-- 訂單庫存扣減 -->
      <div class="form-card">
        <h2 class="form-card-title">
          <i class="ri-box-3-line"></i> 訂單庫存扣減
        </h2>

        <div class="form-group">
          <label class="form-label">庫存扣減模式</label>

          <!-- 簡易庫存模式 分組標題 -->
          <div class="mode-group-label">
            <i class="ri-store-line"></i> 簡易模式（不使用倉庫管理）
          </div>
          <div class="radio-group">
            <label class="radio-item">
              <input type="radio" v-model="form.order_deduct_mode" value="disabled" />
              <span class="radio-content">
                <span class="radio-title">
                  簡易庫存模式
                  <span class="mode-badge simple">推薦新手</span>
                </span>
                <span class="radio-hint">
                  <strong>不需要建立任何倉庫</strong>，直接在「商品管理 → 規格」維護庫存數量。<br>
                  訂單建立時自動從商品庫存數量扣減，開箱即用。
                </span>
              </span>
            </label>
          </div>

          <!-- 進銷存倉庫模式 分組標題 -->
          <div class="mode-group-label mt-12">
            <i class="ri-archive-line"></i> 進銷存模式（啟用倉庫分倉管理）
          </div>
          <div class="radio-group">
            <label class="radio-item">
              <input
                type="radio"
                v-model="form.order_deduct_mode"
                value="specific_warehouse"
                :disabled="!warehousesLoading && !warehouses.length"
              />
              <span class="radio-content">
                <span class="radio-title">指定倉庫</span>
                <span class="radio-hint">無論訂單選哪間門市，統一從指定倉庫扣減（適合中央倉統一出貨）</span>
              </span>
            </label>
            <label class="radio-item">
              <input
                type="radio"
                v-model="form.order_deduct_mode"
                value="store_warehouse"
                :disabled="!warehousesLoading && !warehouses.length"
              />
              <span class="radio-content">
                <span class="radio-title">依門市倉庫扣減</span>
                <span class="radio-hint">根據訂單選擇的門市，從對應倉庫扣減（各門市獨立庫存）</span>
              </span>
            </label>
          </div>
          <div v-if="!warehousesLoading && !warehouses.length" class="warehouse-required-hint">
            <i class="ri-alert-line"></i>
            <span>尚未建立倉庫資訊，請先至少新增一個倉庫。</span>
            <button type="button" class="warehouse-link-btn" @click="goToWarehouseManagement">前往倉庫管理</button>
          </div>
        </div>

        <!-- 指定倉庫下拉 -->
        <div v-if="form.order_deduct_mode === 'specific_warehouse'" class="form-group">
          <label class="form-label">
            指定倉庫 <span class="required">*</span>
          </label>
          <select
            v-model="form.default_warehouse_id"
            class="form-control"
            :required="form.order_deduct_mode === 'specific_warehouse'"
          >
            <option value="" disabled>請選擇倉庫</option>
            <option v-for="wh in warehouses" :key="wh.id" :value="wh.id">
              {{ wh.warehouseName }}
              <template v-if="wh.warehouseType === 'main'">（總倉）</template>
            </option>
          </select>
          <small class="form-hint">訂單完成時，從此倉庫扣減庫存</small>
        </div>

        <!-- 庫存不足行為（所有模式都顯示，因簡易模式也會做庫存檢查） -->
        <div class="form-group">
          <label class="form-label">庫存不足時的行為</label>
          <div class="radio-group">
            <label class="radio-item">
              <input type="radio" v-model="form.insufficient_stock_action" value="confirm" />
              <span class="radio-content">
                <span class="radio-title">詢問是否缺貨下訂<span class="mode-badge confirm">推薦</span></span>
                <span class="radio-hint">庫存不足時彈出提示，讓員工選擇是否先下單後補貨</span>
              </span>
            </label>
            <label class="radio-item">
              <input type="radio" v-model="form.insufficient_stock_action" value="block" />
              <span class="radio-content">
                <span class="radio-title">直接拒絕訂單</span>
                <span class="radio-hint">庫存不足時直接拒絕，不允許建立訂單（嚴格防超賣）</span>
              </span>
            </label>
          </div>
        </div>

      </div><!-- /form-card -->

      <!-- 手動同步（指定倉庫模式可用） -->
      <div v-if="form.order_deduct_mode === 'specific_warehouse'" class="sync-section-inline">
        <div class="sync-header">
          <div>
            <p class="sync-title">手動同步商品庫存數量</p>
            <p class="sync-desc">
              將所有商品規格的庫存數量，重新計算為
              <strong>{{
                form.order_deduct_mode === 'store_warehouse' ? '各門市對應倉庫庫存' : '指定倉庫庫存'
              }}</strong>。
            </p>
          </div>
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="syncing || !form.default_warehouse_id"
            @click="syncStock"
          >
            <i v-if="syncing" class="ri-loader-4-line spin"></i>
            <i v-else class="ri-refresh-line"></i>
            {{ syncing ? '同步中...' : '立即同步' }}
          </button>
        </div>
        <div v-if="syncResult" :class="['sync-result', syncResult.success ? 'sync-ok' : 'sync-fail']">
          <i :class="syncResult.success ? 'ri-check-circle-line' : 'ri-error-warning-line'"></i>
          {{ syncResult.message }}
          <template v-if="syncResult.success && syncResult.count > 0">（共 {{ syncResult.count }} 筆規格）</template>
        </div>
      </div>

        </div><!-- /settings-left -->

        <!-- 右欄：動態情境說明 -->
        <aside class="settings-right">
        <div class="scenario-panel">
          <div class="scenario-header">
            <i class="ri-lightbulb-line"></i>
            <span>目前設定的實際運作方式</span>
          </div>

          <!-- disabled → 簡易庫存模式 -->
          <template v-if="form.order_deduct_mode === 'disabled'">
            <p class="scenario-summary">
              不需設定倉庫。訂單建立時直接從<strong>商品規格的庫存數量欄位</strong>扣減，
              庫存數字在「商品管理 → 規格」中手動維護。
            </p>

            <!-- 庫存來源對比 -->
            <div class="scenario-example">
              <div class="example-title"><i class="ri-database-2-line"></i> 運作方式</div>
              <div class="simple-stock-desc">
                <div class="ssd-row">
                  <span class="ssd-label">需要建立倉庫？</span>
                  <span class="ssd-value highlight"><i class="ri-close-circle-line"></i> 不需要，開箱即用</span>
                </div>
                <div class="ssd-row">
                  <span class="ssd-label">庫存記錄位置</span>
                  <span class="ssd-value">商品管理 → 商品規格 → 庫存數量欄位</span>
                </div>
                <div class="ssd-row">
                  <span class="ssd-label">如何更新庫存</span>
                  <span class="ssd-value">員工直接在商品頁輸入最新數量（進貨後手動更新）</span>
                </div>
                <div class="ssd-row">
                  <span class="ssd-label">訂單如何扣庫</span>
                  <span class="ssd-value">建立訂單時自動從商品庫存數量扣減</span>
                </div>
              </div>
            </div>

            <!-- 操作案例 -->
            <div class="scenario-example">
              <div class="example-title"><i class="ri-book-open-line"></i> 操作案例</div>
              <div class="stock-table">
                <div class="stock-row header">
                  <span>商品規格</span><span>目前庫存數量</span>
                </div>
                <div class="stock-row">
                  <span>商品 A／黑色</span><span>10 件</span>
                </div>
              </div>
              <div class="example-flow mt-12">
                <div class="flow-step">
                  <span class="step-badge">下單</span>
                  <span>員工建立訂單：商品 A 黑色 ×3 件</span>
                </div>
                <div class="flow-arrow"><i class="ri-arrow-down-line"></i></div>
                <div class="flow-step ok">
                  <span class="step-badge ok">扣減</span>
                  <span>商品 A 黑色庫存：<strong>10 → 7 件</strong>（直接扣減商品欄位）</span>
                </div>
              </div>
              <div class="example-flow mt-12">
                <div class="flow-step">
                  <span class="step-badge">下單</span>
                  <span>員工建立訂單：商品 A 黑色 ×20 件（庫存只剩 7）</span>
                </div>
                <div class="flow-arrow"><i class="ri-arrow-down-line"></i></div>
                <div class="flow-step" :class="form.insufficient_stock_action === 'block' ? 'fail' : 'warn'">
                  <span class="step-badge" :class="form.insufficient_stock_action === 'block' ? 'fail' : 'warn'">不足</span>
                  <span v-if="form.insufficient_stock_action === 'confirm'">7 件 &lt; 20 件 → 跳出提示框，員工可選擇「先下單後補貨」</span>
                  <span v-else>7 件 &lt; 20 件 → <strong>直接拒絕訂單</strong></span>
                </div>
              </div>
            </div>

            <div class="scenario-tip">
              <i class="ri-lightbulb-line"></i>
              <strong>適合對象</strong>：剛導入系統的新客戶、小型門市、單一據點、或不需要分倉管理的情境。
              <br>完全不需要建立倉庫資訊，直接在商品頁維護與更新庫存數量即可。
            </div>
          </template>

          <!-- specific_warehouse -->
          <template v-else-if="form.order_deduct_mode === 'specific_warehouse'">
            <p class="scenario-summary">
              無論訂單選哪間門市，<strong>都只從
              <span class="warehouse-name">{{ selectedWarehouseName || '（請先選擇倉庫）' }}</span>
              扣減庫存</strong>。
            </p>
            <div class="scenario-example">
              <div class="example-title"><i class="ri-book-open-line"></i> 操作案例</div>
              <div class="example-flow">
                <div class="flow-step">
                  <span class="step-badge">下單 1</span>
                  <span>員工選<strong>台北門市</strong>，下單商品 A ×3</span>
                </div>
                <div class="flow-arrow"><i class="ri-arrow-down-line"></i></div>
                <div class="flow-step ok">
                  <span class="step-badge ok">扣減</span>
                  <span>從 <strong class="warehouse-name">{{ selectedWarehouseName || '指定倉庫' }}</strong> 扣 3 件</span>
                </div>
              </div>
              <div class="example-flow mt-12">
                <div class="flow-step">
                  <span class="step-badge">下單 2</span>
                  <span>員工選<strong>台中門市</strong>，下單商品 A ×5</span>
                </div>
                <div class="flow-arrow"><i class="ri-arrow-down-line"></i></div>
                <div class="flow-step ok">
                  <span class="step-badge ok">扣減</span>
                  <span>同樣從 <strong class="warehouse-name">{{ selectedWarehouseName || '指定倉庫' }}</strong> 扣 5 件（門市不影響扣減來源）</span>
                </div>
              </div>
            </div>
            <div class="scenario-tip">
              <i class="ri-information-line"></i>
              適合所有訂單都由同一個倉庫出貨的情境（如中央倉或總倉統一出貨）。
            </div>
          </template>

          <!-- store_warehouse -->
          <template v-else-if="form.order_deduct_mode === 'store_warehouse'">
            <p class="scenario-summary">
              訂單選哪間門市，就從<strong>該門市設定的倉庫</strong>扣減庫存。門市與倉庫需事先在「門市管理」中關聯。
            </p>
            <div class="scenario-example">
              <div class="example-title"><i class="ri-book-open-line"></i> 操作案例</div>
              <div class="store-warehouse-map">
                <div class="map-row">
                  <span class="map-store"><i class="ri-store-2-line"></i> 台北門市</span>
                  <span class="map-arrow">→</span>
                  <span class="map-warehouse"><i class="ri-archive-line"></i> 台北倉</span>
                </div>
                <div class="map-row">
                  <span class="map-store"><i class="ri-store-2-line"></i> 台中門市</span>
                  <span class="map-arrow">→</span>
                  <span class="map-warehouse"><i class="ri-archive-line"></i> 台中倉</span>
                </div>
                <div class="map-row warn-row">
                  <span class="map-store"><i class="ri-store-2-line"></i> 高雄門市</span>
                  <span class="map-arrow">→</span>
                  <span class="map-warehouse warn"><i class="ri-alert-line"></i> 未設定倉庫</span>
                </div>
              </div>
              <div class="example-flow mt-12">
                <div class="flow-step">
                  <span class="step-badge">案例一</span>
                  <span>選<strong>台北門市</strong>，下單商品 A ×3</span>
                </div>
                <div class="flow-arrow"><i class="ri-arrow-down-line"></i></div>
                <div class="flow-step ok">
                  <span class="step-badge ok">扣減</span>
                  <span>從<strong>台北倉</strong>扣 3 件</span>
                </div>
              </div>
              <div class="example-flow mt-12">
                <div class="flow-step">
                  <span class="step-badge">案例二</span>
                  <span>選<strong>台中門市</strong>，下單商品 A ×3</span>
                </div>
                <div class="flow-arrow"><i class="ri-arrow-down-line"></i></div>
                <div class="flow-step ok">
                  <span class="step-badge ok">扣減</span>
                  <span>從<strong>台中倉</strong>扣 3 件（各門市獨立庫存）</span>
                </div>
              </div>
              <div class="example-flow mt-12">
                <div class="flow-step">
                  <span class="step-badge">案例三</span>
                  <span>選<strong>高雄門市</strong>（未設倉庫），下單商品 A ×3</span>
                </div>
                <div class="flow-arrow"><i class="ri-arrow-down-line"></i></div>
                <div class="flow-step warn">
                  <span class="step-badge warn">注意</span>
                  <span>訂單成立，但<strong>不扣減庫存</strong>（頁面會顯示警告提示）</span>
                </div>
              </div>
            </div>
            <div class="scenario-tip warn">
              <i class="ri-alert-line"></i>
              使用此模式前，請先至「<strong>門市管理 → 編輯門市 → 進銷存設定</strong>」為每間門市關聯對應倉庫。
            </div>
          </template>
        </div><!-- /scenario-panel -->
        </aside><!-- /settings-right -->

      </div><!-- /settings-layout -->
    </form>

    <!-- ===== 模式切換 Modal ===== -->
    <div v-if="modal.show" class="modal-overlay" @click.self="cancelModal">
      <div class="modal-box">
        <!-- 標題 -->
        <div class="modal-header">
          <i class="ri-alert-line modal-icon-warn"></i>
          <h3 class="modal-title">{{ modal.title }}</h3>
        </div>

        <p class="modal-desc">{{ modal.desc }}</p>

        <!-- 選項：切換至不扣減 -->
        <div v-if="modal.type === 'to-disabled'" class="modal-options">
          <label class="modal-radio-item">
            <input type="radio" v-model="modal.action" value="recalc" />
            <span class="modal-radio-content">
              <span class="modal-radio-title">自動重算商品庫存</span>
              <span class="modal-radio-hint">將各倉庫庫存加總，重新計算到商品庫存欄位（商品欄位 = 所有倉庫加總）</span>
            </span>
          </label>
          <label class="modal-radio-item">
            <input type="radio" v-model="modal.action" value="zero-variant" />
            <span class="modal-radio-content">
              <span class="modal-radio-title">商品庫存歸零</span>
              <span class="modal-radio-hint">將所有商品規格的庫存欄位清空為 0，之後需自行輸入</span>
            </span>
          </label>
          <label class="modal-radio-item">
            <input type="radio" v-model="modal.action" value="keep" />
            <span class="modal-radio-content">
              <span class="modal-radio-title">保留現有商品庫存數量不變</span>
              <span class="modal-radio-hint">維持目前商品規格上的庫存數字，不做任何異動</span>
            </span>
          </label>
        </div>

        <!-- 選項：從不扣減切換至分倉 -->
        <div v-else-if="modal.type === 'from-disabled'" class="modal-options">
          <label class="modal-radio-item">
            <input type="radio" v-model="modal.action" value="migrate" />
            <span class="modal-radio-content">
              <span class="modal-radio-title">將商品庫存併入倉庫</span>
              <span class="modal-radio-hint">把商品規格上的庫存數量，累加至指定倉庫，再將商品欄位歸零</span>
            </span>
          </label>
          <!-- 選擇目標倉庫 + 合併模式 -->
          <div v-if="modal.action === 'migrate'" class="modal-sub-field">
            <div class="sub-field-group">
              <label class="form-label">選擇目標倉庫 <span class="required">*</span></label>
              <select v-model="modal.targetWarehouseId" class="form-control">
                <option value="" disabled>請選擇倉庫</option>
                <option v-for="wh in warehouses" :key="wh.id" :value="wh.id">
                  {{ wh.warehouseName }}
                  <template v-if="wh.warehouseType === 'main'">（總倉）</template>
                </option>
              </select>
            </div>
            <div class="sub-field-group">
              <label class="form-label">倉庫已有同商品庫存時</label>
              <div class="merge-mode-row">
                <label class="merge-mode-item">
                  <input type="radio" v-model="modal.mergeMode" value="add" />
                  <span>
                    <span class="merge-mode-title">累加</span>
                    <span class="merge-mode-hint">舊庫存 + 商品庫存 = 新庫存</span>
                  </span>
                </label>
                <label class="merge-mode-item">
                  <input type="radio" v-model="modal.mergeMode" value="overwrite" />
                  <span>
                    <span class="merge-mode-title">覆蓋</span>
                    <span class="merge-mode-hint">以商品庫存取代舊庫存</span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <label class="modal-radio-item">
            <input type="radio" v-model="modal.action" value="zero-warehouse" />
            <span class="modal-radio-content">
              <span class="modal-radio-title">倉庫庫存歸零</span>
              <span class="modal-radio-hint">將所有倉庫庫存清空為 0，之後再至各倉庫手動輸入</span>
            </span>
          </label>
          <label class="modal-radio-item">
            <input type="radio" v-model="modal.action" value="keep" />
            <span class="modal-radio-content">
              <span class="modal-radio-title">保留現有倉庫庫存不變</span>
              <span class="modal-radio-hint">倉庫庫存維持不動，商品庫存欄位將不影響分倉</span>
            </span>
          </label>
        </div>

        <!-- 執行結果 -->
        <div v-if="modal.result" :class="['modal-result', modal.result.success ? 'sync-ok' : 'sync-fail']">
          <i :class="modal.result.success ? 'ri-check-circle-line' : 'ri-error-warning-line'"></i>
          {{ modal.result.message }}
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="cancelModal" :disabled="modal.executing">
            取消
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="modal.executing || !modal.action || (modal.action === 'migrate' && !modal.targetWarehouseId)"
            @click="confirmModal"
          >
            <span v-if="modal.executing">處理中...</span>
            <span v-else>確認並繼續</span>
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings'
import { warehouseService } from '@/api/modules/warehouses'

export default {
  name: 'InventorySettings',
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const saving  = ref(false)
    const saved   = ref(false)
    const syncing = ref(false)
    const syncResult = ref(null)
    const warehousesLoading = ref(false)

    const form = reactive({
      order_deduct_mode:         'disabled',  // disabled | specific_warehouse | store_warehouse
      default_warehouse_id:      null,
      insufficient_stock_action: 'confirm',
    })

    // 記錄原始模式，用來偵測「是否真的有切換」
    const originalMode = ref('disabled')

    const warehouses = ref([])

    const selectedWarehouseName = computed(() => {
      if (!form.default_warehouse_id) return ''
      const wh = warehouses.value.find(w => w.id === form.default_warehouse_id)
      return wh ? wh.warehouseName : ''
    })

    // Modal 狀態
    const modal = reactive({
      show:             false,
      type:             '',    // 'to-disabled' | 'from-disabled'
      title:            '',
      desc:             '',
      action:           '',    // 使用者選擇的動作
      targetWarehouseId: '',   // migrate 時的目標倉庫
      mergeMode:        'add', // 'add' | 'overwrite'
      executing:        false,
      result:           null,
    })

    async function fetchWarehouses() {
      warehousesLoading.value = true
      try {
        const res = await warehouseService.getWarehouses({ status: 'active', pageSize: 100 })
        warehouses.value = res?.data?.items ?? []
      } catch {
        warehouses.value = []
      } finally {
        warehousesLoading.value = false
      }
    }

    const isFirstTime = ref(false)  // 從未儲存過設定

    async function fetchSettings() {
      loading.value = true
      try {
        const res = await clientSettingsService.get(NAMESPACES.INVENTORY)
        const s = res?.data?.settings ?? {}
        form.order_deduct_mode         = s.order_deduct_mode         ?? 'disabled'
        if (form.order_deduct_mode === 'all_warehouses') {
          form.order_deduct_mode = s.default_warehouse_id ? 'specific_warehouse' : 'store_warehouse'
        }
        form.default_warehouse_id      = s.default_warehouse_id      ?? null
        form.insufficient_stock_action = s.insufficient_stock_action ?? 'confirm'
        originalMode.value             = form.order_deduct_mode
        // updatedAt 為 null 表示此客戶從未儲存過進銷存設定
        isFirstTime.value = !res?.data?.updatedAt
      } catch (e) {
        console.error('載入進銷存設定失敗', e)
      } finally {
        loading.value = false
      }
    }

    // ── 存檔主流程 ─────────────────────────────────────────
    async function handleSave() {
      if (!ensureWarehouseReadyForInventoryMode()) return

      if (form.order_deduct_mode === 'specific_warehouse' && !form.default_warehouse_id) {
        alert('請選擇指定倉庫')
        return
      }

      const prev = originalMode.value
      const next = form.order_deduct_mode

      // 偵測是否跨越「不扣減」邊界
      const toDisabled   = prev !== 'disabled' && next === 'disabled'
      const fromDisabled = prev === 'disabled'  && next !== 'disabled'

      if (toDisabled) {
        openModal('to-disabled')
        return  // 等 Modal 確認後再存
      }
      if (fromDisabled) {
        openModal('from-disabled')
        return
      }

      // 同模式切換（all ↔ specific）或無切換，直接存
      await doSave()
    }

    function goToWarehouseManagement() {
      router.push({ name: 'ClientWarehouses' })
    }

    function ensureWarehouseReadyForInventoryMode() {
      if (form.order_deduct_mode === 'disabled') return true
      if (warehousesLoading.value) {
        alert('倉庫資料載入中，請稍後再試')
        return false
      }
      if (warehouses.value.length > 0) return true

      const shouldNavigate = window.confirm(
        '尚未建立倉庫資訊，請先至少新增一個倉庫，再設定進銷存模式。是否前往倉庫管理？'
      )
      if (shouldNavigate) goToWarehouseManagement()
      return false
    }

    function openModal(type) {
      modal.show             = true
      modal.type             = type
      modal.action           = ''
      modal.targetWarehouseId = ''
      modal.mergeMode        = 'add'
      modal.executing        = false
      modal.result           = null

      if (type === 'to-disabled') {
        modal.title = '切換至「不扣減」模式'
        modal.desc  = '切換後訂單將不再自動扣減庫存。請選擇現有倉庫庫存的處理方式：'
      } else {
        const label = form.order_deduct_mode === 'store_warehouse' ? '依門市倉庫扣減' : '指定倉庫'
        modal.title = `切換至「${label}」扣減模式`
        modal.desc  = '切換後訂單將自動扣減倉庫庫存。請選擇現有商品庫存的處理方式：'
      }
    }

    function cancelModal() {
      if (modal.executing) return
      modal.show = false
    }

    async function confirmModal() {
      if (!modal.action) return
      if (modal.action === 'migrate' && !modal.targetWarehouseId) return

      modal.executing = true
      modal.result    = null

      try {
        // 先儲存設定
        await doSave()

        // 再執行庫存搬遷動作
        if (modal.action !== 'keep') {
          await executeMigration()
        }

        originalMode.value = form.order_deduct_mode
        modal.show = false
      } catch (e) {
        modal.result = {
          success: false,
          message: e?.response?.data?.message ?? e.message,
        }
      } finally {
        modal.executing = false
      }
    }

    async function executeMigration() {
      let res, count, label

      switch (modal.action) {
        case 'recalc':
          // 倉庫加總 → 商品欄位
          res   = await clientSettingsService.syncInventoryStock('all_warehouses', null)
          count = res?.data?.updatedCount ?? 0
          label = `已重算 ${count} 筆商品規格庫存`
          break

        case 'zero-variant':
          res   = await clientSettingsService.clearVariantStock()
          count = res?.data?.clearedCount ?? 0
          label = `已將 ${count} 筆商品規格庫存歸零`
          break

        case 'migrate':
          res   = await clientSettingsService.migrateToWarehouse(modal.targetWarehouseId, modal.mergeMode)
          count = res?.data?.migratedCount ?? 0
          label = `已將 ${count} 筆規格庫存${modal.mergeMode === 'add' ? '累加' : '覆蓋'}至倉庫`
          break

        case 'zero-warehouse':
          res   = await clientSettingsService.clearWarehouseStock()
          count = res?.data?.clearedCount ?? 0
          label = `已將 ${count} 筆倉庫庫存歸零`
          break
      }

      modal.result = { success: true, message: label }

      // 短暫顯示結果後關閉
      await new Promise(r => setTimeout(r, 1800))
    }

    // ── 實際呼叫 API 存檔 ─────────────────────────────────
    async function doSave() {
      saving.value = true
      saved.value  = false
      try {
        await clientSettingsService.set(NAMESPACES.INVENTORY, {
          order_deduct_mode:         form.order_deduct_mode,
          default_warehouse_id:      form.order_deduct_mode === 'specific_warehouse'
                                       ? form.default_warehouse_id : null,
          insufficient_stock_action: form.order_deduct_mode !== 'disabled'
                                       ? form.insufficient_stock_action : 'confirm',
        })
        saved.value = true
        isFirstTime.value = false  // 儲存後隱藏首次提示
        setTimeout(() => { saved.value = false }, 3000)
      } finally {
        saving.value = false
      }
    }

    // ── 手動同步（分倉 → 商品欄位） ───────────────────────
    async function syncStock() {
      syncing.value   = true
      syncResult.value = null
      try {
        const res = await clientSettingsService.syncInventoryStock('specific_warehouse', form.default_warehouse_id)
        syncResult.value = {
          success: true,
          message: res?.data?.message ?? '已同步完成',
          count:   res?.data?.updatedCount ?? 0,
        }
      } catch (e) {
        syncResult.value = {
          success: false,
          message: '同步失敗：' + (e?.response?.data?.message ?? e.message),
        }
      } finally {
        syncing.value = false
      }
    }

    onMounted(() => Promise.all([fetchWarehouses(), fetchSettings()]))

    return {
      loading, saving, saved, syncing, syncResult,
      form, warehouses, warehousesLoading, originalMode,
      selectedWarehouseName,
      isFirstTime,
      modal,
      handleSave, cancelModal, confirmModal, syncStock, goToWarehouseManagement,
    }
  },
}
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.settings-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ── 兩欄佈局 ── */
.settings-layout {
  display: grid;
  grid-template-columns: minmax(0, 600px) minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.settings-left {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.settings-right {
  position: sticky;
  top: 80px; /* 固定在右側，隨捲動保持可見 */
}

/* 窄螢幕：單欄堆疊 */
@media (max-width: 1024px) {
  .settings-layout {
    grid-template-columns: 1fr;
  }
  .settings-right {
    position: static;
  }
}

.form-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 24px;
}

.form-card-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.required { color: #ef4444; }

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #111827;
  background: #fff;
  outline: none;
  transition: border-color 0.2s;
}

.form-control:focus { border-color: #2563eb; }

.form-hint {
  display: block;
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

/* ── Radio 群組 ── */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.radio-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.radio-item:has(input:checked) {
  border-color: #2563eb;
  background: #eff6ff;
}

.radio-item input[type="radio"] {
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: #2563eb;
}

.radio-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.radio-title {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

.radio-hint {
  font-size: 12px;
  color: #6b7280;
}

/* ── 模式分組標題 ── */
.mode-group-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
  margin-top: 4px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e5e7eb;
}
.mode-group-label.mt-12 { margin-top: 16px; }

.warehouse-required-hint {
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid #fed7aa;
  border-radius: 6px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.warehouse-link-btn {
  background: transparent;
  border: none;
  padding: 0;
  color: #1d4ed8;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
}

/* ── 模式 badge ── */
.mode-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  margin-left: 6px;
  vertical-align: middle;
}
.mode-badge.simple {
  background: #d1fae5;
  color: #065f46;
}
.mode-badge.confirm {
  background: #dbeafe;
  color: #1e40af;
}

/* ── 簡易庫存說明表 ── */
.simple-stock-desc {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  font-size: 13px;
}
.ssd-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 8px 14px;
  border-bottom: 1px solid #f0f0f0;
}
.ssd-row:last-child { border-bottom: none; }
.ssd-label {
  width: 108px;
  flex-shrink: 0;
  font-weight: 500;
  color: #374151;
  font-size: 12px;
}
.ssd-value { color: #4b5563; }
.ssd-value.highlight { color: #059669; font-weight: 600; }

/* ── 儲存按鈕 ── */
.form-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.save-success {
  font-size: 14px;
  color: #16a34a;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ── 手動同步區塊 ── */
.sync-section-inline {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sync-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.sync-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 4px;
}

.sync-desc {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

.sync-result {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  padding: 8px 12px;
  border-radius: 6px;
}

.sync-ok  { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
.sync-fail { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }

/* ── Loading ── */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
  color: #6b7280;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 12px;
}

.spin { animation: spin 0.8s linear infinite; display: inline-block; }

@keyframes spin { to { transform: rotate(360deg); } }

/* ── Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}

.modal-box {
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.modal-icon-warn {
  font-size: 24px;
  color: #d97706;
  flex-shrink: 0;
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.modal-desc {
  font-size: 14px;
  color: #4b5563;
  margin: 0 0 20px;
  line-height: 1.6;
}

.modal-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.modal-radio-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.modal-radio-item:has(input:checked) {
  border-color: #2563eb;
  background: #eff6ff;
}

.modal-radio-item input[type="radio"] {
  margin-top: 3px;
  flex-shrink: 0;
  accent-color: #2563eb;
}

.modal-radio-content {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.modal-radio-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.modal-radio-hint {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

.modal-sub-field {
  margin: 4px 0 4px 24px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sub-field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sub-field-group .form-label {
  margin-bottom: 0;
  font-size: 13px;
}

.merge-mode-row {
  display: flex;
  gap: 10px;
}

.merge-mode-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  flex: 1;
  transition: border-color 0.2s, background 0.2s;
}

.merge-mode-item:has(input:checked) {
  border-color: #2563eb;
  background: #eff6ff;
}

.merge-mode-item input[type="radio"] {
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: #2563eb;
}

.merge-mode-item > span {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.merge-mode-title {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}

.merge-mode-hint {
  font-size: 11px;
  color: #6b7280;
}

.modal-result {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  padding: 10px 12px;
  border-radius: 6px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

@media (max-width: 640px) {
  .form-card { padding: 16px; }
  .sync-header { flex-direction: column; }
  .modal-box { padding: 20px; }
}

/* ── 新客戶首次設定提示 ── */
.first-time-banner {
  display: flex;
  gap: 14px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
  padding: 16px;
}

.ftb-icon {
  flex-shrink: 0;
  font-size: 22px;
  color: #d97706;
  margin-top: 1px;
}

.ftb-body {
  flex: 1;
}

.ftb-title {
  font-size: 14px;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 8px;
}

.ftb-defaults {
  margin: 0 0 8px;
  padding-left: 18px;
  font-size: 13px;
  color: #78350f;
  line-height: 1.8;
}

.ftb-hint {
  margin: 0;
  font-size: 12px;
  color: #b45309;
}

/* ── 動態情境說明面板 ── */
.scenario-panel {
  border: 1px solid #c7d2fe;
  border-radius: 10px;
  background: #f5f7ff;
  overflow: hidden;
}

.scenario-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #e0e7ff;
  font-size: 13px;
  font-weight: 600;
  color: #3730a3;
}

.scenario-header i {
  font-size: 16px;
  color: #6366f1;
}

.scenario-summary {
  padding: 12px 16px 0;
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
  margin: 0;
}

.scenario-example {
  margin: 12px 16px;
  background: #fff;
  border: 1px solid #e0e7ff;
  border-radius: 8px;
  padding: 14px;
}

.example-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #6366f1;
  margin-bottom: 12px;
}

.example-flow {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.example-flow.mt-12 {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e5e7eb;
}

.flow-step {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: #374151;
  padding: 6px 8px;
  border-radius: 6px;
  background: #f9fafb;
}

.flow-step.ok { background: #f0fdf4; }
.flow-step.warn { background: #fffbeb; }
.flow-step.fail { background: #fff1f2; }
.flow-step.neutral { background: #f3f4f6; }

.flow-arrow {
  text-align: center;
  color: #9ca3af;
  font-size: 12px;
  padding-left: 10px;
}

.step-badge {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 99px;
  background: #e0e7ff;
  color: #3730a3;
  white-space: nowrap;
}

.step-badge.ok { background: #dcfce7; color: #166534; }
.step-badge.warn { background: #fef3c7; color: #92400e; }
.step-badge.fail { background: #fee2e2; color: #991b1b; }
.step-badge.neutral { background: #e5e7eb; color: #374151; }

/* 庫存表格 */
.stock-table {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  font-size: 13px;
}

.stock-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 6px 12px;
  border-bottom: 1px solid #f3f4f6;
}

.stock-row:last-child { border-bottom: none; }
.stock-row.header { background: #f9fafb; font-weight: 600; color: #6b7280; font-size: 12px; }
.stock-row.total { background: #eff6ff; font-weight: 600; color: #1d4ed8; }

/* 門市倉庫對應表 */
.store-warehouse-map {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 4px;
}

.map-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  padding: 6px 10px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.map-row.warn-row {
  background: #fffbeb;
  border-color: #fcd34d;
}

.map-store { color: #374151; font-weight: 500; flex: 1; }
.map-arrow { color: #9ca3af; }
.map-warehouse { color: #1d4ed8; }
.map-warehouse.warn { color: #b45309; }

/* 情境提示 */
.scenario-tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 12px 16px;
  padding: 10px 12px;
  font-size: 12px;
  color: #374151;
  background: #f0f9ff;
  border-left: 3px solid #38bdf8;
  border-radius: 0 6px 6px 0;
  line-height: 1.6;
}

.scenario-tip.warn {
  background: #fffbeb;
  border-left-color: #f59e0b;
  color: #78350f;
}

.scenario-tip i {
  font-size: 14px;
  color: #38bdf8;
  margin-top: 1px;
  flex-shrink: 0;
}

.scenario-tip.warn i { color: #f59e0b; }

.warehouse-name {
  color: #2563eb;
  font-weight: 600;
}
</style>
